import { Observable, of } from 'rxjs';

import {
    ChatActivity,
    ChatMessage,
    ChatOutgoingMessage,
    ChatPageHasMore,
    ChatSessionState,
} from '../../types/chat-session.types';

/** Абстрактная база для одной открытой сессии чата (транспорт + метаданные). */
export abstract class BaseChatSession {
    /** Идентификатор сессии. */
    public abstract readonly id: string;

    /** Заголовок сессии (имя контакта, название ассистента). */
    public abstract readonly title: string;

    /** Текущее окно загруженных сообщений (одна страница). */
    public abstract readonly messages$: Observable<ChatMessage[]>;

    /**
     * Есть ли соседние страницы истории.
     * `before` — более старые (кнопка сверху), `after` — ранее показанные более новые (кнопка снизу).
     */
    public abstract readonly hasMore$: Observable<ChatPageHasMore>;

    /** Идёт ли запрос соседней страницы истории. */
    public abstract readonly isLoadingMore$: Observable<ChatPageHasMore>;

    /** ID первого непрочитанного — anchor для скролла. null если всё прочитано. */
    public abstract readonly firstUnreadId$: Observable<string | null>;

    /** Счётчик непрочитанных сообщений. */
    public abstract readonly unreadCount$: Observable<number>;

    /** Текущая активность в чате (typing, thinking, …). null — чат молчит. */
    public abstract readonly activity$: Observable<ChatActivity | null>;

    /**
     * true, пока микрофон нужно скрыть: расшифровка голоса и/или ожидание ответа агента после голоса.
     * По умолчанию false — сессии без голосового ввода.
     */
    public readonly voiceInputLocked$: Observable<boolean> = of(false);

    /** Состояние соединения. */
    public abstract readonly state$: Observable<ChatSessionState>;

    /** Отправить сообщение. */
    public abstract send(message: ChatOutgoingMessage): void;

    /**
     * Сменить страницу истории.
     * `before` — более старые, `after` — вернуть более новые / хвост.
     * Текущее окно заменяется, а не дополняется.
     */
    public abstract loadMore(direction: 'before' | 'after'): void;

    /** Сдвинуть курсор прочитанного. Вызывается когда сообщение входит в viewport. */
    public abstract markReadUpTo(messageId: string): void;

    /** Уведомить собеседника о вводе. */
    public abstract notifyTyping(isTyping: boolean): void;

    /** Прервать текущую активную генерацию ответа, если транспорт это поддерживает. */
    public abstract cancel(): void;

    /** Закрыть соединение и освободить ресурсы. */
    public abstract destroy(): void;
}
